import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { MoneyInput, Select, TextInput } from '~/components';
import { COMP_TYPE_OPTIONS } from '../../consts';
import {
  useNegotiationBaseline,
  useNegotiationInputsDisabled,
  useNegotiationResponseType,
} from '../../provider';
import { TermCompareSection } from '../../sub-components/TermCompareSection';

const TAGS = CT.employer.negotiation;

export const BaseCompensationSection = () => {
  const inputsDisabled = useNegotiationInputsDisabled();
  const responseType = useNegotiationResponseType();
  const [termsChanged, setTermsChanged] = useState({
    compType: false,
    pricePerHour: false,
    pricePerShift: false,
    pricePerRvu: false,
    yearlyRvusPerFte: false,
  });
  const negotiation = useNegotiationBaseline();
  const [compType, pricePerHour, pricePerShift, pricePerRvu, yearlyRvusPerFte] = useWatch({
    name: ['compType', 'pricePerHour', 'pricePerShift', 'pricePerRvu', 'yearlyRvusPerFte'],
  });

  useEffect(() => {
    setTermsChanged({
      compType: compType.value !== negotiation?.compType?.value,
      pricePerHour: pricePerHour !== Number(negotiation?.pricePerHour),
      pricePerShift: pricePerShift !== Number(negotiation?.pricePerShift),
      pricePerRvu: pricePerRvu !== Number(negotiation?.pricePerRvu),
      yearlyRvusPerFte: yearlyRvusPerFte !== negotiation?.yearlyRvusPerFte,
    });
  }, [compType, pricePerHour, pricePerShift, pricePerRvu, yearlyRvusPerFte, negotiation]);

  const changedTermsCount = Object.entries(termsChanged).filter(
    ([field, changed]) => changed && (responseType === 'msaas' || field !== 'yearlyRvusPerFte')
  ).length;

  return (
    <TermCompareSection
      icon={<CurrencyDollarIcon className="h-5 w-5" />}
      changedTermsCount={changedTermsCount}
      title="Base Compensation"
      classname="mb-4"
      terms={[
        {
          label: 'Compensation Type',
          value: negotiation?.compType?.label,
          newValueField: (
            <Select
              name={'compType'}
              options={COMP_TYPE_OPTIONS}
              flagged={termsChanged.compType}
              disabled={inputsDisabled}
              cyTag={TAGS.compensationForm.compensationTypeSelect}
            />
          ),
        },
        {
          label: 'Price per Hour',
          value: negotiation?.pricePerHour,
          prefix: '$',
          newValueField: (
            <MoneyInput
              name={'pricePerHour'}
              flagged={termsChanged.pricePerHour}
              disabled={inputsDisabled}
              cyTag={TAGS.compensationForm.pricePerHourInput}
            />
          ),
        },
        {
          label: 'Price per Shift',
          value: negotiation?.pricePerShift,
          prefix: '$',
          newValueField: (
            <MoneyInput
              name={'pricePerShift'}
              flagged={termsChanged.pricePerShift}
              disabled={inputsDisabled}
              cyTag={TAGS.compensationForm.pricePerShiftInput}
            />
          ),
        },
        {
          label: 'Price per RVU',
          value: negotiation?.pricePerRvu,
          prefix: '$',
          newValueField: (
            <MoneyInput
              name={'pricePerRvu'}
              flagged={termsChanged.pricePerRvu}
              disabled={inputsDisabled}
              cyTag={TAGS.compensationForm.pricePerRVUInput}
            />
          ),
        },
        ...(responseType === 'msaas'
          ? [
              {
                label: 'Yearly RVUs per FTE',
                value: negotiation?.yearlyRvusPerFte,
                newValueField: (
                  <TextInput
                    type="number"
                    name={'yearlyRvusPerFte'}
                    flagged={termsChanged.yearlyRvusPerFte}
                    disabled={inputsDisabled}
                    cyTag={TAGS.compensationForm.yearlyRVUInput}
                  />
                ),
              },
            ]
          : []),
      ]}
    />
  );
};
